import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Button, message, Modal, Spin } from 'antd';
// import moment from 'moment';
import html2canvas from 'html2canvas';
// import copy from 'copy-to-clipboard';
import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { getInviteOrderInfo } from '@/api/crsInvitation';
import { parseFen2Yuan } from '@/utils/number';
import CountDownText from '@/components/CountDownText';
import { renderIntlLabel } from '@/utils';
import { EXPORT_DATE_TIME_FORMAT } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';

import InviteContent from './InviteContent';
import { getPrintCss, getPrintHtml } from './InviteContent/generateDynamicHtml';
import { InviteDetail } from './helper';
import styles from './index.module.scss';

type InviteModalProps = PropsWithChildren<{
  orderNo: string;
  open: boolean;
  onClose: () => void;
}>;

const win: any = window;
let iframe = null;

const InviteModal: React.FC<InviteModalProps> = ({ orderNo, open, onClose }) => {
  const [detail, setDetail] = useState<InviteDetail>();
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailLoadSuccess, setDetailLoadSuccess] = useState<boolean | null>(null);

  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [generateImgLoading, setGenerateImgLoading] = useState<boolean>(false);
  // const [copyLoading, setCopyLoading] = useState<boolean>(false);

  const contentRef = useRef<any>();

  const initPaymentDetail = async () => {
    try {
      setDetailLoading(true);
      // 初始状态
      setDetailLoadSuccess(null);
      const res: any = await getInviteOrderInfo({ orderNo });
      if (res?.code === 0) {
        setDetail({ ...res?.data, qrcodeUrl: res?.data.sortUrl });
        setDetailLoadSuccess(true);
      } else {
        setDetailLoadSuccess(false);
      }

      setDetailLoadSuccess(true);
    } catch (error) {
      setDetailLoadSuccess(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const electronPrint = async (cssStr, htmlStr) => {
    win?.electron?.ipcRenderer?.sendPrintData({
      cssStr,
      htmlStr,
    });
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    let imgSrc = '';
    // 拿到二维码URL
    try {
      const codeCanvas = await html2canvas(contentRef.current.getScanCodeRef(), {
        useCORS: true,
        scale: 4,
      });
      imgSrc = codeCanvas.toDataURL('png');
    } catch (error) {
      setPrintLoading(false);
    }

    const cssStr = getPrintCss();
    const htmlStr = getPrintHtml({ ...detail, qrcodeSrc: imgSrc || '' });
    if (win.electron) {
      electronPrint(cssStr, htmlStr);
    } else {
      try {
        if (iframe) {
          document.body.removeChild(iframe);
        }
        // 等待图片加载完成
        const image = new Image();
        image.src = imgSrc;
        image.onload = () => {
          iframe = document.createElement('iframe');
          document.body.appendChild(iframe);
          iframe.contentDocument.head.innerHTML = cssStr;
          iframe.contentDocument.body.innerHTML = htmlStr;
          iframe.contentWindow.print();
          setPrintLoading(false);
        };
      } catch (error) {
        //
      } finally {
        setPrintLoading(false);
      }
    }
  };

  const generateFileName = () => {
    const { userName, orderAmount, commonCode, operateTime } = detail || {};
    const fileName = `${userName}_${parseFen2Yuan(orderAmount)}_${renderIntlLabel(
      'order_crs_label_crs_invate_order',
    )}_${commonCode}_${getTimeByUtcOffset(operateTime, EXPORT_DATE_TIME_FORMAT)}.png`;

    return fileName;
  };

  const handleDownload = async () => {
    try {
      setGenerateImgLoading(true);
      const canvas = await html2canvas(contentRef.current.getChildrenRef(), {
        useCORS: true,
        scale: 4,
      });
      const imgSrc = canvas.toDataURL('png');
      if (imgSrc) {
        if (win?.electron) {
          win?.electron?.ipcRenderer?.download({
            downloadPath: imgSrc, // 下载链接
            fileName: generateFileName(), // 下载文件名，需要包含后缀名
          });
        } else {
          const a = document.createElement('a');
          a.href = imgSrc;
          a.download = generateFileName();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      //
    } finally {
      setGenerateImgLoading(false);
    }
  };

  // const handleCopy = () => {
  //   setCopyLoading(true);
  //   const isSuccess = copy(detail?.orderH5Url);
  //   if (isSuccess) {
  //     message.success(renderIntlLabel('order_crs_msg_send_copied_link'));
  //     setCopyLoading(false);
  //   } else {
  //     setCopyLoading(false);
  //   }
  // };

  const handleCancel = () => {
    if (iframe) {
      try {
        document.body.removeChild(iframe);
      } catch (error) {
        //
      }
    }

    if (onClose) {
      onClose();
    }
  };

  const handleRetry = () => {
    initPaymentDetail();
  };

  useEffect(() => {
    if (open && orderNo) {
      initPaymentDetail();
      try {
        win?.electron?.ipcRenderer.on('print-result', ({ success }) => {
          setPrintLoading(false);
          if (success) {
            message.success(renderIntlLabel('order_crs_msg_print_success'));
          } else {
            message.error(renderIntlLabel('order_crs_msg_print_fail'));
          }
        });
      } catch (error) {
        setPrintLoading(false);
      }
    }
  }, [open, orderNo]);

  const renderContentByLoadingStatus = () => {
    if (detailLoading || detailLoadSuccess === null) {
      return (
        <>
          <div className={styles.iconContainer}>
            <SyncOutlined spin className={styles.tipIcon} />
            <div>{renderIntlLabel('order_crs_generating')}</div>
          </div>
        </>
      );
    }
    if (detailLoadSuccess) {
      return (
        <>
          <div className={styles.tip}>
            {renderIntlLabel('order_crs_tip_provide_inviteOrder_to_guest_scan')}
          </div>
          <div className={styles.time}>
            <span>{renderIntlLabel('order_crs_msg_remain_pay_time')}：</span>
            <span style={{ color: '#ED6A0C' }}>
              <CountDownText totalTime={detail.remainPayTime} />
            </span>
          </div>
          <div className={styles.contentWrapper}>
            <Spin spinning={printLoading || generateImgLoading}>
              <InviteContent ref={contentRef} detail={detail} />
            </Spin>
          </div>
        </>
      );
    }
    if (detailLoadSuccess === false) {
      return (
        <>
          <div className={styles.iconContainer}>
            <CloseOutlined className={styles.tipIcon} />
            <div>{renderIntlLabel('order_crs_msg_generate_fail_to_retry')}</div>
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Modal
        title={renderIntlLabel('order_crs_label_crs_invate_order')}
        destroyOnClose
        forceRender
        maskClosable={false}
        width={560}
        open={open}
        onCancel={handleCancel}
        wrapClassName={styles.modal}
        bodyStyle={{ padding: '24px 24px 20px' }}
        footer={
          <>
            {detailLoadSuccess ? (
              <>
                <Button type="ghost" onClick={handleCancel}>
                  {renderIntlLabel('order_action_cancel')}
                </Button>
                <Button loading={printLoading} type="primary" onClick={handlePrint}>
                  {renderIntlLabel('order_action_print')}
                </Button>
                <Button loading={generateImgLoading} type="primary" onClick={handleDownload}>
                  {renderIntlLabel('order_action_download')}
                </Button>
                {/* <Button loading={copyLoading} type="primary" onClick={handleCopy}>
                  {renderIntlLabel('order_action_copy_link')}
                </Button> */}
              </>
            ) : (
              <>
                <Button type="ghost" onClick={handleCancel}>
                  {renderIntlLabel('order_action_cancel')}
                </Button>
                <Button type="primary" onClick={handleRetry}>
                  {renderIntlLabel('order_action_retry')}
                </Button>
              </>
            )}
          </>
        }
      >
        <div className={styles.container}>{renderContentByLoadingStatus()}</div>
      </Modal>
    </>
  );
};

export default InviteModal;
